// AdminLayout — Dark theme admin shell with sidebar nav + top bar

import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';

const NAV = [
  { to: '/admin/dashboard',     label: 'Dashboard',    icon: '📊', badge: null },
  { to: '/admin/trading-hall',  label: 'Trading hall', icon: '🏛️', badge: null },
  { to: '/admin/users',         label: 'Users',        icon: '👥', badge: '12.4K' },
  { to: '/admin/kyc',           label: 'KYC',          icon: '🛡️', badge: '23' },
  { to: '/admin/trades',        label: 'Trades',       icon: '📈', badge: null },
  { to: '/admin/payouts',       label: 'Payouts',      icon: '💰', badge: '8' },
  { to: '/admin/bots',          label: 'Bots',         icon: '🤖', badge: null },
  { to: '/admin/content',       label: 'Content',      icon: '💬', badge: '4' },
  { to: '/admin/broadcast',     label: 'Broadcast',    icon: '📣', badge: null },
  { to: '/admin/audit',         label: 'Audit log',    icon: '🔍', badge: null },
  { to: '/admin/settings',      label: 'Settings',     icon: '⚙️', badge: null },
];

const NAV_OPS = [
  { to: '/admin/challenges',    label: 'Challenges',    icon: '🎯' },
  { to: '/admin/resets',        label: 'Resets',        icon: '🔁' },
  { to: '/admin/certificates',  label: 'Certificates',  icon: '🏆' },
  { to: '/admin/billing',       label: 'Billing',       icon: '💳' },
  { to: '/admin/violations',    label: 'Violations',    icon: '⛔' },
  { to: '/admin/consistency',   label: 'Consistency',   icon: '📊' },
  { to: '/admin/risk-flags',    label: 'Risk flags',    icon: '🚩' },
  { to: '/admin/margin',        label: 'Margin',        icon: '🛡️' },
  { to: '/admin/news-blackouts',label: 'News blackouts',icon: '📰' },
  { to: '/admin/auto-flatten',  label: 'Auto-flatten',  icon: '⏰' },
  { to: '/admin/discord',       label: 'Discord',       icon: '💬' },
];

export function AdminLayout({ onLogout }: { onLogout: () => void }) {
  const nav = useNavigate();
  const loc = useLocation();

  return (
    <div className="min-h-screen flex" style={{ background: '#0A0A0A' }}>
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/5 bg-[#0F0F0F]">
        <div className="flex items-center gap-2 px-4 h-14 border-b border-white/5">
          <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-gradient-to-br from-[#FF3B30] to-[#FF9500] text-white text-[16px]">🛡️</div>
          <div>
            <div className="text-[13px] font-bold text-white">Funder Pro</div>
            <div className="text-[9px] text-white/40">Admin console</div>
          </div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) => ['flex items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-[13px] transition', isActive ? 'bg-white/10 text-white font-semibold' : 'text-white/60 hover:bg-white/5 hover:text-white'].join(' ')}
            >
              <span className="text-[16px]">{n.icon}</span>
              <span className="flex-1">{n.label}</span>
              {n.badge && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white/80">{n.badge}</span>}
            </NavLink>
          ))}
          <div className="px-2 pt-3 pb-1.5 text-[9px] font-bold uppercase tracking-wider text-white/30">Operations</div>
          {NAV_OPS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`op-${n.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) => ['flex items-center gap-2.5 rounded-[8px] px-2.5 py-1.5 text-[12px] transition', isActive ? 'bg-white/10 text-white font-semibold' : 'text-white/50 hover:bg-white/5 hover:text-white'].join(' ')}
            >
              <span className="text-[14px]">{n.icon}</span>
              <span className="flex-1">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/5 p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#FF3B30] to-[#FF9500] text-white text-[11px] font-bold">SA</div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-white truncate">Super Admin</div>
              <div className="text-[10px] text-white/40 truncate">admin@funderpro.live</div>
            </div>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="flex w-full items-center justify-center rounded-[8px] bg-white/5 px-3 py-1.5 text-[12px] font-semibold text-white/80 active:bg-white/10">
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl px-3">
          <div className="md:hidden flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white" onClick={() => nav('/admin/dashboard')}>🛡️</div>
          <div className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" /></svg>
              <input type="text" placeholder="Search users, trades, KYC..." data-testid="admin-search" className="h-9 w-full rounded-[8px] bg-white/5 border border-white/10 pl-9 pr-3 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF3B30]" />
            </div>
          </div>
          <button onClick={() => alert('Notifications')} data-testid="admin-notifs" className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white active:bg-white/10">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
            <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
          </button>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-white/50">
            <span className="h-1.5 w-1.5 rounded-full bg-[#34C759] animate-pulse" />
            <span>Live</span>
          </div>
        </header>

        {/* Mobile horizontal nav */}
        <nav className="md:hidden flex items-center gap-1 overflow-x-auto border-b border-white/5 bg-[#0F0F0F] px-2 py-1.5">
          {NAV.slice(0, 6).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`m-nav-${n.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) => ['shrink-0 flex flex-col items-center gap-0.5 rounded-[8px] px-2.5 py-1.5 transition', isActive ? 'bg-white/10 text-white' : 'text-white/50'].join(' ')}
            >
              <span className="text-[16px]">{n.icon}</span>
              <span className="text-[10px] font-semibold">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Page body */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6" key={loc.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
